





























import Vue from 'vue'
import { getInRoomLabel, hasUnaddressedLiveAlert, isInAnyRoom, isInOwnRoom } from '@/utils/patients'
import PatientCallout from '@/components/map/PatientCallout.vue'
import StatusIcon from '@/components/map/StatusIcon.vue'
import SummaryReport from '@/components/resident-summary/SummaryReport.vue'

export default Vue.extend({
  components: { StatusIcon, PatientCallout, SummaryReport },
  props: {
    patient: {
      type: Object,
      default: null,
    },
    roomNumber: {
      type: String,
      default: null,
    },
    searchQuery: {
      type: String,
      required: true,
    },
    width: {
      type: Number,
      default: 100,
    },
    height: {
      type: Number,
      default: null,
    },
    enlarged: {
      type: Boolean,
      default: false,
    },
    mini: {
      type: Boolean,
      default: false,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    anchor: {
      type: String,
      default: 'right',
    },

    textOffsetLeft: {
      type: Number,
      default: null,
    },
    textOffsetTop: {
      type: Number,
      default: null,
    },

    /* call out */
    calloutMarginBottom: {
      type: Number,
      default: -15,
    },

    /* cut outs */
    cutTopLeft: {
      type: Boolean,
      default: false,
    },
    cutBottomLeft: {
      type: Boolean,
      default: false,
    },
    cutBottomRight: {
      type: Boolean,
      default: false,
    },
    cutTopRight: {
      type: Boolean,
      default: false,
    },
    cutHeight: {
      type: Number,
      default: 0,
    },
    cutWidth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      resident: this.patient,

      calloutVisible: false,
      patientSummaryVisible: false,
      patientSummaryShowBelow: true,
      patientSummaryOffsetTop: 0,
    }
  },
  computed: {
    searched() {
      return this.searchQuery == this.roomNumber
    },
    roomCssVars() {
      return {
        '--room-margin': this.mini ? '7px' : '0',
        '--room-height': (this.height ? this.height + 'px' : null) || 'auto',
        '--room-width': (this.width || 100) + 'px',
        '--callout-offset': (this.width || 100) * 0.85 - 15 + 'px',

        '--room-background-color': this.patient ? '#ffffff' : '#f5f5f5',
        '--bb-width': this.mini ? this.width + 'px' : '100%',
        '--room-text-margin-top': this.mini ? '-2.5px' : this.isInThisRoom ? '-5.5px' : '-7.5px',
        '--room-cursor': this.patient && this.patient.id ? 'pointer' : 'not-allowed',

        '--right-callout-offset-left': this.anchor.toLowerCase() == 'right' ? 'unset' : '70px',
        '--right-callout-offset-right': this.anchor.toLowerCase() == 'right' ? '-335px' : 'unset',

        '--summary-popup-offset-top': this.patientSummaryOffsetTop + 'px',
      }
    },
    maskCss() {
      let borderWidth = this.isInThisRoom ? '2px' : '4px'
      let borderOffset = '-' + borderWidth

      return {
        '--cut-height': this.cutHeight + 'px',
        '--cut-width': this.cutWidth + 'px',

        '--mask-top': this.cutTop ? 0 : 'unset',
        '--mask-right': this.cutLeft ? 'unset' : 0,
        '--mask-bottom': this.cutTop ? 'unset' : 0,
        '--mask-left': this.cutLeft ? 0 : 'unset',

        '--mask-margin-top': this.cutTop ? borderOffset : 'auto',
        '--mask-margin-right': this.cutLeft ? 'auto' : borderOffset,
        '--mask-margin-bottom': this.cutTop ? 'auto' : borderOffset,
        '--mask-margin-left': this.cutLeft ? borderOffset : 'auto',

        '--cut-border-top': this.cutTop ? 0 : borderWidth,
        '--cut-border-right': this.cutLeft ? borderWidth : 0,
        '--cut-border-bottom': this.cutTop ? borderWidth : 0,
        '--cut-border-left': this.cutLeft ? 0 : borderWidth,

        '--mask-display': this.cutTop || this.cutLeft ? 'auto' : 'none',
      }
    },
    roomNumberCssVars() {
      let borderOffset = this.isInThisRoom ? 0 : -2 // move up and left if there's a border...
      return {
        '--text-margin-top': (this.textOffsetTop && this.textOffsetTop + borderOffset + 'px') || 'unset',
        '--text-margin-left': (this.textOffsetLeft && this.textOffsetLeft + borderOffset + 'px') || 'unset',
      }
    },
    cutTop() {
      return this.cutTopLeft || this.cutTopRight
    },
    cutLeft() {
      return this.cutTopLeft || this.cutBottomLeft
    },
    roomClasses() {
      let inOutClass = ''
      if (this.isInThisRoom) {
        inOutClass = 'In-Room'
      } else {
        inOutClass = 'Out-Room'
      }
      let anchorClass = this.anchor.toLowerCase() === 'right' ? 'AnchorRight' : 'AnchorLeft'
      return [
        'Room',
        'text-h6',
        'ma-0',
        inOutClass,
        anchorClass,
        this.outerRoomBorder,
        this.vertical ? 'vertical-room' : 'horizontal-room',
      ]
    },
    roomBorderClasses() {
      if (this.isInThisRoom) {
        return ['Room-Border']
      } else {
        if (this.mini) {
          return ['Room-Border', 'Out-Room-Border-Mini']
        } else {
          return ['Room-Border', 'Out-Room-Border']
        }
      }
    },
    outerRoomBorder() {
      if (this.isInThisRoom) {
        return null
      } else {
        if (this.mini) {
          return 'Out-Room-Border-Mini'
        } else {
          return 'Out-Room-Border'
        }
      }
    },

    isInThisRoom(): boolean {
      if (this.patient) {
        return this.patient && isInOwnRoom(this.patient)
      }
      return true
    },
    isInAnyRoom(): boolean {
      if (this.patient) {
        return this.patient && isInAnyRoom(this.patient)
      }
      return true
    },
    inRoomDescription(): string {
      if (!this.patient) {
        return this.isInThisRoom ? 'In room (test)' : 'Not in room (test)'
      }
      return getInRoomLabel(this.patient)
    },
    roomLabel(): string {
      return this.roomNumber
    },
    labelClasses(): string[] {
      let classes = []
      if (!this.patient) {
        classes.push('Room-Number-Empty')
      }
      if (this.textOffsetLeft || this.textOffsetTop) {
        classes.push('Room-Number-Custom-Position')
      } else {
        classes.push('Room-Number-Centered')
      }
      return classes
    },
    calloutClassList() {
      if (!this.calloutVisible) {
        return 'invisibleCallout'
      } else {
        return ['visibleCallout', 'rounded-lg', 'pa-1']
      }
    },
    liveEvent() {
      return this.patient && this.patient.liveEvents && this.patient.liveEvents[0]
    },
    hasUnaddressedLiveAlert() {
      return this.liveEvent && hasUnaddressedLiveAlert(this.patient.id, this.liveEvent)
    },
  },
  methods: {
    mouseOver: function() {
      this.calloutVisible = true
    },
    mouseOut: function() {
      this.calloutVisible = false
    },
    getMapRect() {
      return document.getElementById('patient-map').getBoundingClientRect()
    },
    offsetTopInMap() {
      let thisTop = this.$el.getBoundingClientRect().top
      let patientMapTop = this.getMapRect().top
      return (thisTop - patientMapTop) * 1.25 // because LBMF is scale 0.8
    },
    isInTopHalfOfMap() {
      return this.offsetTopInMap() < this.getMapRect().height / 2
    },
    roomClick() {
      if (this.patient) {
        // calculated here so we know it's already rendered
        if (this.enlarged && !this.isInTopHalfOfMap()) {
          //enlarged map takes up more vertical space so top offset is larger
          this.patientSummaryOffsetTop = -this.offsetTopInMap() + 250
        } else {
          this.patientSummaryOffsetTop = -this.offsetTopInMap() + 10
        }
        this.patientSummaryVisible = true
      }
    },
    closeReport() {
      setTimeout(() => (this.patientSummaryVisible = false), 0)
    },
  },
})
